import { useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { SupplierPeriodFilter } from "@/components/supplier/SupplierPeriodFilter";
import { formatCents } from "@/lib/currency";
import { formatQuantityDisplay, getQuantityUnitLabel } from "@/lib/quantity";
import { QUOTATION_UNIT_LABELS } from "@/features/quotation/unitLabels";
import type { UnitType } from "@/features/quotation/types";
import {
  useSupplierPedidos,
  sortByProduct,
  sortByStore,
  filterProducts,
  filterStores,
  applyProductFilter,
  applyStoreFilter,
  type ProductSortKey,
  type StoreSortKey,
} from "@/features/supplierPedidos/hooks";
import type { SupplierStoreRow } from "@/features/supplierPedidos/types";
import {
  formatSupplierUnitSummary,
  getSupplierOrderItemDisplayQuantity,
  getSupplierProductDisplayQuantity,
} from "@/features/supplierPedidos/displayQuantities";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import "./SupplierPedidosPage.css";

function quantity(amount: number, unit: string) {
  return `${formatQuantityDisplay(amount, unit)} ${getQuantityUnitLabel(unit)}`;
}

function StoreRow({ row }: { row: SupplierStoreRow }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="supplier-store">
      <button
        type="button"
        className="aggregate-row supplier-store-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="supplier-row-identity">
          <strong>{row.store_name}</strong>
          <span className="cell-sub">
            {row.client_name} ·{" "}
            {formatSupplierUnitSummary(row.quantities) || "Sem volumes"}
          </span>
        </span>
        <span className="mono">
          {formatSupplierUnitSummary(row.quantities) || "—"}
        </span>
        <strong className="mono supplier-row-total">
          {formatCents(row.total_brl)}{" "}
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </strong>
      </button>
      {open && (
        <table className="supplier-store-items">
          <thead>
            <tr>
              <th>Produto</th>
              <th>Quantidade</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            {row.items.map((item, index) => (
              <tr key={`${item.product_name}-${index}`}>
                <td>
                  {item.product_name}{" "}
                  <span className="supplier-unit">
                    {QUOTATION_UNIT_LABELS[item.unit_type as UnitType] ??
                      item.unit_type}
                  </span>
                </td>
                <td className="mono">
                  {quantity(
                    getSupplierOrderItemDisplayQuantity(item),
                    item.unit_type,
                  )}
                </td>
                <td className="mono">{formatCents(item.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

type Options = { value: string; label: string }[];
function FilterModal({
  productOptions,
  storeOptions,
  clientOptions,
  filterProduct,
  filterStore,
  filterClient,
  onApply,
  onClose,
}: {
  productOptions: Options;
  storeOptions: Options;
  clientOptions: Options;
  filterProduct: string;
  filterStore: string;
  filterClient: string;
  onApply: (product: string, store: string, client: string) => void;
  onClose: () => void;
}) {
  const [product, setProduct] = useState(filterProduct);
  const [store, setStore] = useState(filterStore);
  const [client, setClient] = useState(filterClient);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, onClose);
  return (
    <div
      className="supplier-filter-layer"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          event.preventDefault();
          onClose();
        }
      }}
    >
      <div
        className="supplier-filter-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filtrar"
      >
        <header>
          <h2>Filtrar</h2>
          <button type="button" aria-label="Fechar filtros" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        {(
          [
            [
              "Produto",
              product,
              setProduct,
              productOptions,
              "Todos os produtos",
            ],
            ["Loja", store, setStore, storeOptions, "Todas as lojas"],
            ["Cliente", client, setClient, clientOptions, "Todos os clientes"],
          ] as const
        ).map(([label, value, setValue, options, all]) => (
          <label className="supplier-filter-field" key={label}>
            {label}
            <select
              value={value}
              onChange={(event) => setValue(event.target.value)}
            >
              <option value="">{all}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
        <footer>
          {(product || store || client) && (
            <button
              type="button"
              onClick={() => {
                setProduct("");
                setStore("");
                setClient("");
              }}
            >
              Limpar filtros
            </button>
          )}
          <button
            type="button"
            className="supplier-primary"
            onClick={() => {
              onApply(product, store, client);
              onClose();
            }}
          >
            Aplicar
          </button>
        </footer>
      </div>
    </div>
  );
}

export function SupplierPedidosPage() {
  const [period, setPeriod] = useState("today");
  const [customRange, setCustomRange] = useState<{
    from: string;
    to: string;
  }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [productSort, setProductSort] = useState<ProductSortKey>("value");
  const [storeSort, setStoreSort] = useState<StoreSortKey>("name");
  const [productOpen, setProductOpen] = useState(true);
  const [storeOpen, setStoreOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterProduct, setFilterProduct] = useState("");
  const [filterStore, setFilterStore] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const { data, isLoading, isError } = useSupplierPedidos(period, customRange);
  const filteredProducts = useMemo(
    () =>
      sortByProduct(
        applyProductFilter(
          filterProducts(data?.by_product ?? [], searchQuery),
          filterProduct,
        ),
        productSort,
      ),
    [data?.by_product, searchQuery, filterProduct, productSort],
  );
  const filteredStores = useMemo(
    () =>
      sortByStore(
        applyStoreFilter(
          filterStores(data?.by_store ?? [], searchQuery),
          filterStore,
          filterClient,
        ),
        storeSort,
      ),
    [data?.by_store, searchQuery, filterStore, filterClient, storeSort],
  );
  const productOptions = useMemo(
    () =>
      (data?.by_product ?? []).map((p) => ({
        value: p.product_id,
        label: p.product_name,
      })),
    [data?.by_product],
  );
  const storeOptions = useMemo(
    () =>
      (data?.by_store ?? []).map((s) => ({
        value: s.store_id,
        label: s.store_name,
      })),
    [data?.by_store],
  );
  const clientOptions = useMemo(
    () =>
      [...new Set((data?.by_store ?? []).map((s) => s.client_name))].map(
        (name) => ({ value: name, label: name }),
      ),
    [data?.by_store],
  );
  const productStoreCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of data?.by_product ?? []) {
      // Store items have no product_id: duplicate product names with the same unit are ambiguous.
      if (
        data?.by_product.filter(
          (row) =>
            row.product_name === product.product_name &&
            row.unit_type === product.unit_type,
        ).length !== 1
      )
        continue;
      const stores = new Set<string>();
      for (const store of data?.by_store ?? []) {
        for (const item of store.items) {
          if (
            item.product_name === product.product_name &&
            item.unit_type === product.unit_type
          ) {
            stores.add(store.store_id);
          }
        }
      }
      if (stores.size) {
        counts.set(product.product_id, stores.size);
      }
    }
    return counts;
  }, [data?.by_product, data?.by_store]);
  const isDetailed = data?.is_detailed_view ?? true;
  const activeFilterCount = [filterProduct, filterStore, filterClient].filter(
    Boolean,
  ).length;
  return (
    <section className="supplier-pedidos-page app-page-frame">
      <header className="supplier-page-header">
        <h1>Pedidos</h1>
      </header>
      <div className="supplier-periods">
        <SupplierPeriodFilter
          period={period}
          onChange={(next) => {
            setPeriod(next);
            setSearchQuery("");
          }}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </div>
      {isError && (
        <p role="alert">
          Não foi possível carregar os pedidos. Tente novamente.
        </p>
      )}
      {isDetailed && !isLoading && (
        <div className="supplier-toolbar">
          <label className="supplier-search">
            <Search size={17} />
            <input
              type="search"
              aria-label="Pesquisar produtos, lojas ou clientes"
              placeholder="Pesquisar produtos, lojas ou clientes"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="supplier-filter-trigger"
            aria-label="Filtrar"
            onClick={() => setFilterOpen(true)}
          >
            <SlidersHorizontal size={17} />
            <span>Filtros</span>
            {activeFilterCount > 0 && <b>{activeFilterCount}</b>}
          </button>
        </div>
      )}
      <div className="supplier-summary">
        <div>
          <span>Volumes</span>
          <strong>
            {isLoading || isError || !data
              ? "—"
              : formatSupplierUnitSummary(data.quantities) ||
                (data.quantities?.length
                  ? data.quantities
                      .map((item) =>
                        quantity(
                          item.ordered_quantity ?? item.quantity,
                          item.unit_type,
                        ),
                      )
                      .join(" · ")
                  : data.total_boxes === 0
                    ? "0"
                    : "—")}
          </strong>
        </div>
        <div>
          <span>Total faturamento</span>
          <strong>
            {isLoading || isError || !data ? "—" : formatCents(data.total_brl)}
          </strong>
        </div>
      </div>
      <section className="aggregate">
        <button
          type="button"
          className="aggregate-head"
          aria-expanded={productOpen}
          onClick={() => setProductOpen(!productOpen)}
        >
          Por produto{" "}
          {productOpen ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
        </button>
        {productOpen && (
          <>
            {isDetailed && (
              <div className="supplier-sort">
                <span>Ordenar por:</span>
                <button
                  type="button"
                  aria-pressed={productSort === "value"}
                  onClick={() => setProductSort("value")}
                >
                  Valor ↓
                </button>
                <button
                  type="button"
                  aria-pressed={productSort === "alpha"}
                  onClick={() => setProductSort("alpha")}
                >
                  Alfabético
                </button>
              </div>
            )}
            {!isDetailed ? (
              <p className="supplier-empty">
                Selecione um período de até 90 dias para ver o detalhamento por
                produto e loja.
              </p>
            ) : isLoading ? (
              <p className="supplier-empty">Carregando...</p>
            ) : filteredProducts.length === 0 ? (
              <p className="supplier-empty">Nenhum produto encontrado.</p>
            ) : (
              filteredProducts.map((p) => (
                <div className="aggregate-row" key={p.product_id}>
                  <div className="supplier-row-identity">
                    <strong>{p.product_name}</strong>
                    <span className="cell-sub">
                      {productStoreCounts.has(p.product_id)
                        ? `${productStoreCounts.get(p.product_id)} ${productStoreCounts.get(p.product_id) === 1 ? "loja" : "lojas"}`
                        : (QUOTATION_UNIT_LABELS[p.unit_type as UnitType] ??
                          p.unit_type)}
                    </span>
                  </div>
                  <span className="mono">
                    {quantity(
                      getSupplierProductDisplayQuantity(p),
                      p.unit_type,
                    )}
                  </span>
                  <strong className="mono">{formatCents(p.total_brl)}</strong>
                </div>
              ))
            )}
          </>
        )}
      </section>
      <section className="aggregate">
        <button
          type="button"
          className="aggregate-head"
          aria-expanded={storeOpen}
          onClick={() => setStoreOpen(!storeOpen)}
        >
          Por loja{" "}
          {storeOpen ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
        </button>
        {storeOpen && (
          <>
            {isDetailed && (
              <div className="supplier-sort">
                <span>Ordenar por:</span>
                <button
                  type="button"
                  aria-pressed={storeSort === "name"}
                  onClick={() => setStoreSort("name")}
                >
                  Nome Loja
                </button>
                <button
                  type="button"
                  aria-pressed={storeSort === "value"}
                  onClick={() => setStoreSort("value")}
                >
                  Valor ↓
                </button>
              </div>
            )}
            {!isDetailed ? (
              <p className="supplier-empty">
                Selecione um período de até 90 dias para ver o detalhamento por
                produto e loja.
              </p>
            ) : isLoading ? (
              <p className="supplier-empty">Carregando...</p>
            ) : filteredStores.length === 0 ? (
              <p className="supplier-empty">Nenhuma loja encontrada.</p>
            ) : (
              filteredStores.map((row) => (
                <StoreRow key={row.order_id} row={row} />
              ))
            )}
          </>
        )}
      </section>
      {filterOpen && (
        <FilterModal
          productOptions={productOptions}
          storeOptions={storeOptions}
          clientOptions={clientOptions}
          filterProduct={filterProduct}
          filterStore={filterStore}
          filterClient={filterClient}
          onApply={(product, store, client) => {
            setFilterProduct(product);
            setFilterStore(store);
            setFilterClient(client);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </section>
  );
}
